import { useEffect, useId, useState, type FormEvent } from 'react'
import { apiErrorMessage } from '../../api/client'
import { ErrorMessage } from '../ErrorMessage'
import { CodeBoxes } from './CodeBoxes'

/**
 * The second step of every sign-in and sign-up: "we emailed you a code - type it in". There are no
 * passwords (V46); this is the whole of proving who you are.
 *
 * Used by the app's sign-in and sign-up forms, the ops console and the aggregator portal, so all of
 * them read and behave the same: six boxes for the 6-digit code, a resend that unlocks after a
 * minute, and a way back to change the address.
 */
export function JoinCodeStep({
  sentTo,
  onVerify,
  onResend,
  onBack,
  submitLabel = 'Continue',
  codeLabel = 'Code from your email',
  buttonClassName = 'btn-sun w-full',
  resendAfterSeconds = 60,
}: {
  /** The address the code went to, shown so a typo is noticed. */
  sentTo: string
  onVerify: (code: string) => Promise<void>
  onResend: () => Promise<void>
  onBack?: () => void
  submitLabel?: string
  /** The line above the boxes. */
  codeLabel?: string
  buttonClassName?: string
  resendAfterSeconds?: number
}) {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [wait, setWait] = useState(resendAfterSeconds)
  const labelId = useId()

  useEffect(() => {
    if (wait <= 0) return
    const t = window.setTimeout(() => setWait((w) => w - 1), 1000)
    return () => window.clearTimeout(t)
  }, [wait])

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (code.length !== 6) {
      setError('Enter the 6 digit code from the email')
      return
    }
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      await onVerify(code)
    } catch (err) {
      setError(apiErrorMessage(err, 'That code did not work'))
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  async function resend() {
    setError(null)
    setNotice(null)
    try {
      await onResend()
      setNotice('A new code is on its way.')
      setWait(resendAfterSeconds)
      setCode('')
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send a new code'))
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <span id={labelId} className="mb-2 block text-sm font-medium text-ink-700">
          {codeLabel}
        </span>
        <CodeBoxes value={code} onChange={setCode} autoFocus disabled={busy} labelledBy={labelId} />
        <p className="mt-2.5 text-sm text-ink-500">
          We sent a 6 digit code to <span className="break-all text-ink-700">{sentTo}</span>. It expires in 10
          minutes.
        </p>
      </div>

      <ErrorMessage message={error} />
      {notice && <p className="text-sm text-green-700">{notice}</p>}

      <button type="submit" disabled={busy || code.length !== 6} className={buttonClassName}>
        {busy ? 'Checking...' : submitLabel}
      </button>

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <button
          type="button"
          onClick={() => void resend()}
          disabled={wait > 0}
          className="font-semibold text-ink-900 hover:underline disabled:cursor-default disabled:font-normal disabled:text-ink-400 disabled:no-underline"
        >
          {wait > 0 ? `Send a new code in ${wait}s` : 'Send a new code'}
        </button>
        {onBack && (
          <button type="button" onClick={onBack} className="font-semibold text-ink-900 hover:underline">
            Use a different email
          </button>
        )}
      </div>
    </form>
  )
}
